// Premium features that can be entitled to users
export enum PremiumFeature {
  UNLIMITED_DAILY_QUIZZES = 'unlimited_quiz',
  AD_FREE_EXPERIENCE = 'ad_free_experience',
  UNLIMITED_QUIZ_HISTORY = 'unlimited_quiz_history',
  MULTIPLE_QUESTION_FORMATS = 'multiple_question_formats',
  MULTI_LANGUAGE_SUPPORT = 'multi_language_support',
  FAMILY_ANALYTICS = 'family_analytics',
  MULTIPLAYER_CREATE = 'multiplayer_create',
  MULTIPLAYER_JOIN = 'multiplayer_join',
  GAME_ROOM_CREATE = 'game_room_create',
  GAME_ROOM_JOIN = 'game_room_join',
  TEAM_MANAGEMENT = 'team_management',
  PREMIUM_QUIZ_PACKS = 'premium_quiz_packs',
  PREMIUM_LEADER_RESOURCES = 'premium_leader_resources',

  // Retained only for compatibility with dormant legacy code paths.
  LIVE_EVENT_CREATE = 'live_event_create',
  LIVE_EVENT_JOIN = 'live_event_join',
  GROUP_BRANDING = 'group_branding',
  GROUP_ANALYTICS = 'group_analytics',
  GROUP_MEMBER_MANAGEMENT = 'group_member_management',
  GROUP_EVENT_HOSTING = 'group_event_hosting',
  PRIORITY_SUPPORT = 'priority_support',
  ADVANCED_ANALYTICS = 'advanced_analytics',
  SOCIAL_MANUAL_SHARE = 'social_manual_share',
  SOCIAL_SCHEDULER_BASIC = 'social_scheduler_basic',
  SOCIAL_SCHEDULER_PRO = 'social_scheduler_pro',
  SOCIAL_UNLIMITED = 'social_unlimited',
  SOCIAL_AI_FACTORY = 'social_ai_factory',
  SOCIAL_ENTERPRISE = 'social_enterprise',
}

export enum SubscriptionTier {
  FREE = 'free',
  PREMIUM = 'premium',

  // Legacy tiers kept so old records and stale utilities can be normalized safely.
  INDIVIDUAL = 'individual',
  FAMILY = 'family',
  GROUP_SMALL = 'group_small',
  GROUP_MEDIUM = 'group_medium',
  GROUP_LARGE = 'group_large',
}

export type ActiveConsumerTier = SubscriptionTier.FREE | SubscriptionTier.PREMIUM;

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  tier: ActiveConsumerTier;
  price: {
    monthly: number;
    annual: number;
  };
  features: PremiumFeature[];
  limits: {
    quizHistory?: number;
    familyMembers?: number;
    concurrentMultiplayerRooms?: number;
    concurrentGameRooms?: number;
    storageGB?: number;
  };
  stripePriceIds: {
    monthly: string;
    annual: string;
  };
  popular?: boolean;
  legacyAliases?: string[];
}

export const PREMIUM_FAMILY_MEMBER_LIMIT = 5;

const FREE_FEATURES: PremiumFeature[] = [
  PremiumFeature.MULTIPLAYER_JOIN,
  PremiumFeature.GAME_ROOM_JOIN,
];

const PREMIUM_FEATURES: PremiumFeature[] = [
  PremiumFeature.UNLIMITED_DAILY_QUIZZES,
  PremiumFeature.AD_FREE_EXPERIENCE,
  PremiumFeature.UNLIMITED_QUIZ_HISTORY,
  PremiumFeature.MULTIPLE_QUESTION_FORMATS,
  PremiumFeature.MULTI_LANGUAGE_SUPPORT,
  PremiumFeature.MULTIPLAYER_CREATE,
  PremiumFeature.MULTIPLAYER_JOIN,
  PremiumFeature.GAME_ROOM_CREATE,
  PremiumFeature.GAME_ROOM_JOIN,
  PremiumFeature.FAMILY_ANALYTICS,
  PremiumFeature.TEAM_MANAGEMENT,
  PremiumFeature.PREMIUM_QUIZ_PACKS,
];

export const LEGACY_PAID_TIERS: SubscriptionTier[] = [
  SubscriptionTier.INDIVIDUAL,
  SubscriptionTier.FAMILY,
  SubscriptionTier.GROUP_SMALL,
  SubscriptionTier.GROUP_MEDIUM,
  SubscriptionTier.GROUP_LARGE,
];

export const normalizeSubscriptionTier = (
  tier?: SubscriptionTier | string | null,
): ActiveConsumerTier => {
  if (!tier || tier === SubscriptionTier.FREE) {
    return SubscriptionTier.FREE;
  }

  if (tier === SubscriptionTier.PREMIUM) {
    return SubscriptionTier.PREMIUM;
  }

  if (LEGACY_PAID_TIERS.includes(tier as SubscriptionTier)) {
    return SubscriptionTier.PREMIUM;
  }

  return SubscriptionTier.FREE;
};

export const TIER_FEATURES: Record<SubscriptionTier, PremiumFeature[]> = {
  [SubscriptionTier.FREE]: FREE_FEATURES,
  [SubscriptionTier.PREMIUM]: PREMIUM_FEATURES,

  // Legacy paid tiers now collapse into the single Premium feature set.
  [SubscriptionTier.INDIVIDUAL]: PREMIUM_FEATURES,
  [SubscriptionTier.FAMILY]: PREMIUM_FEATURES,
  [SubscriptionTier.GROUP_SMALL]: PREMIUM_FEATURES,
  [SubscriptionTier.GROUP_MEDIUM]: PREMIUM_FEATURES,
  [SubscriptionTier.GROUP_LARGE]: PREMIUM_FEATURES,
};

export const FEATURE_DESCRIPTIONS: Record<PremiumFeature, string> = {
  [PremiumFeature.UNLIMITED_DAILY_QUIZZES]: 'Unlimited Bible quiz play across every category.',
  [PremiumFeature.AD_FREE_EXPERIENCE]: 'A clean, ad-free quiz experience.',
  [PremiumFeature.UNLIMITED_QUIZ_HISTORY]: 'Full access to your results, progress, and quiz history.',
  [PremiumFeature.MULTIPLE_QUESTION_FORMATS]: 'Unlock every premium question format.',
  [PremiumFeature.MULTI_LANGUAGE_SUPPORT]: 'Study in every supported language.',
  [PremiumFeature.FAMILY_ANALYTICS]: 'Track household progress and celebrate growth together.',
  [PremiumFeature.MULTIPLAYER_CREATE]: 'Create private quiz rooms for friends and family.',
  [PremiumFeature.MULTIPLAYER_JOIN]: 'Join private quiz rooms by invite.',
  [PremiumFeature.GAME_ROOM_CREATE]: 'Host larger private game-room sessions for your household.',
  [PremiumFeature.GAME_ROOM_JOIN]: 'Join invited game-room sessions.',
  [PremiumFeature.TEAM_MANAGEMENT]: 'Invite up to 5 family members under your Premium plan.',
  [PremiumFeature.PREMIUM_QUIZ_PACKS]: 'Access premium quiz packs and curated content.',
  [PremiumFeature.PREMIUM_LEADER_RESOURCES]: 'Legacy premium leader resources.',
  [PremiumFeature.LIVE_EVENT_CREATE]: 'Legacy hosted event access.',
  [PremiumFeature.LIVE_EVENT_JOIN]: 'Legacy hosted event entry.',
  [PremiumFeature.GROUP_BRANDING]: 'Legacy group branding.',
  [PremiumFeature.GROUP_ANALYTICS]: 'Legacy group analytics.',
  [PremiumFeature.GROUP_MEMBER_MANAGEMENT]: 'Legacy participant management.',
  [PremiumFeature.GROUP_EVENT_HOSTING]: 'Legacy hosted event tools.',
  [PremiumFeature.PRIORITY_SUPPORT]: 'Priority support.',
  [PremiumFeature.ADVANCED_ANALYTICS]: 'Advanced analytics.',
  [PremiumFeature.SOCIAL_MANUAL_SHARE]: 'Legacy manual social sharing.',
  [PremiumFeature.SOCIAL_SCHEDULER_BASIC]: 'Legacy basic social scheduler.',
  [PremiumFeature.SOCIAL_SCHEDULER_PRO]: 'Legacy pro social scheduler.',
  [PremiumFeature.SOCIAL_UNLIMITED]: 'Legacy unlimited social scheduler.',
  [PremiumFeature.SOCIAL_AI_FACTORY]: 'Legacy AI social factory.',
  [PremiumFeature.SOCIAL_ENTERPRISE]: 'Legacy enterprise social suite.',
};

export enum FeatureCategory {
  CORE = 'core',
  CONTENT = 'content',
  ANALYTICS = 'analytics',
  MANAGEMENT = 'management',
  LEGACY = 'legacy',
}

export const FEATURE_CATEGORIES: Record<PremiumFeature, FeatureCategory> = {
  [PremiumFeature.UNLIMITED_DAILY_QUIZZES]: FeatureCategory.CORE,
  [PremiumFeature.AD_FREE_EXPERIENCE]: FeatureCategory.CORE,
  [PremiumFeature.UNLIMITED_QUIZ_HISTORY]: FeatureCategory.CORE,
  [PremiumFeature.MULTIPLE_QUESTION_FORMATS]: FeatureCategory.CORE,
  [PremiumFeature.MULTI_LANGUAGE_SUPPORT]: FeatureCategory.CORE,
  [PremiumFeature.FAMILY_ANALYTICS]: FeatureCategory.ANALYTICS,
  [PremiumFeature.MULTIPLAYER_CREATE]: FeatureCategory.CORE,
  [PremiumFeature.MULTIPLAYER_JOIN]: FeatureCategory.CORE,
  [PremiumFeature.GAME_ROOM_CREATE]: FeatureCategory.MANAGEMENT,
  [PremiumFeature.GAME_ROOM_JOIN]: FeatureCategory.CORE,
  [PremiumFeature.TEAM_MANAGEMENT]: FeatureCategory.MANAGEMENT,
  [PremiumFeature.PREMIUM_QUIZ_PACKS]: FeatureCategory.CONTENT,
  [PremiumFeature.PREMIUM_LEADER_RESOURCES]: FeatureCategory.LEGACY,
  [PremiumFeature.LIVE_EVENT_CREATE]: FeatureCategory.LEGACY,
  [PremiumFeature.LIVE_EVENT_JOIN]: FeatureCategory.LEGACY,
  [PremiumFeature.GROUP_BRANDING]: FeatureCategory.LEGACY,
  [PremiumFeature.GROUP_ANALYTICS]: FeatureCategory.LEGACY,
  [PremiumFeature.GROUP_MEMBER_MANAGEMENT]: FeatureCategory.LEGACY,
  [PremiumFeature.GROUP_EVENT_HOSTING]: FeatureCategory.LEGACY,
  [PremiumFeature.PRIORITY_SUPPORT]: FeatureCategory.LEGACY,
  [PremiumFeature.ADVANCED_ANALYTICS]: FeatureCategory.LEGACY,
  [PremiumFeature.SOCIAL_MANUAL_SHARE]: FeatureCategory.LEGACY,
  [PremiumFeature.SOCIAL_SCHEDULER_BASIC]: FeatureCategory.LEGACY,
  [PremiumFeature.SOCIAL_SCHEDULER_PRO]: FeatureCategory.LEGACY,
  [PremiumFeature.SOCIAL_UNLIMITED]: FeatureCategory.LEGACY,
  [PremiumFeature.SOCIAL_AI_FACTORY]: FeatureCategory.LEGACY,
  [PremiumFeature.SOCIAL_ENTERPRISE]: FeatureCategory.LEGACY,
};

export const getFeaturesByCategory = (category: FeatureCategory): PremiumFeature[] => {
  return Object.entries(FEATURE_CATEGORIES)
    .filter(([, currentCategory]) => currentCategory === category)
    .map(([feature]) => feature as PremiumFeature);
};

export const isTierFeature = (tier: SubscriptionTier, feature: PremiumFeature): boolean => {
  return TIER_FEATURES[tier].includes(feature);
};

export const getMinimumTierForFeature = (feature: PremiumFeature): ActiveConsumerTier | null => {
  if (TIER_FEATURES[SubscriptionTier.FREE].includes(feature)) {
    return SubscriptionTier.FREE;
  }

  if (TIER_FEATURES[SubscriptionTier.PREMIUM].includes(feature)) {
    return SubscriptionTier.PREMIUM;
  }

  return null;
};
